import { FC, useState } from "react"
import { Document } from "@/types"
import { Button } from "@/components/ui/button"
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { EnhancedCheckbox } from "@/components/ui/enhanced-checkbox"
import { Trash2, ArrowDown, ArrowUp, ArrowDownUp } from "lucide-react"
import { DocumentRow } from "./DocumentRow"
import { BatchDocumentStagesResponse } from "@/hooks/useBatchDocumentStages"
import { SortField, SortDirection } from "@/hooks/usePaginatedDocuments"

interface DocumentsVerticalListProps {
	caseId: string
	documents: Document[]
	onDeleteDocument: (documentId: string) => Promise<void>
	onBatchDeleteDocuments: (documentIds: string[]) => Promise<void>
	maxHeight?: string
	stagesData?: BatchDocumentStagesResponse
	sortField: SortField
	sortDirection: SortDirection
	onSort: (field: SortField) => void
}

export const DocumentsVerticalList: FC<DocumentsVerticalListProps> = ({
	caseId,
	documents,
	onDeleteDocument,
	onBatchDeleteDocuments,
	maxHeight = "calc(100vh - 300px)",
	stagesData,
	sortField,
	sortDirection,
	onSort,
}) => {
	const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(
		new Set()
	)
	const [isBatchDeleting, setIsBatchDeleting] = useState(false)

	// Check if any document has search relevance info
	const hasSearchRelevance = documents.some((doc) => doc.relevance_to_search)

	const handleToggleSelect = (documentId: string) => {
		setSelectedDocumentIds((prev) => {
			const newSelected = new Set(prev)
			if (newSelected.has(documentId)) {
				newSelected.delete(documentId)
			} else {
				newSelected.add(documentId)
			}
			return newSelected
		})
	}

	const handleSelectAll = () => {
		if (selectedDocumentIds.size === documents.length) {
			// If all are selected, deselect all
			setSelectedDocumentIds(new Set())
		} else {
			// Otherwise select all
			setSelectedDocumentIds(new Set(documents.map((doc) => doc.id)))
		}
	}

	const selectedDocuments = documents.filter((doc) =>
		selectedDocumentIds.has(doc.id)
	)

	const handleConfirmBatchDelete = async () => {
		setIsBatchDeleting(true)
		const idsToDelete = Array.from(selectedDocumentIds)
		try {
			await onBatchDeleteDocuments(idsToDelete)
			setSelectedDocumentIds(new Set())
		} catch (error) {
			console.error("Batch delete failed:", error)
		} finally {
			setIsBatchDeleting(false)
		}
	}

	// Calculate indeterminate state for "select all" checkbox
	const allSelected =
		selectedDocumentIds.size === documents.length && documents.length > 0
	const someSelected =
		selectedDocumentIds.size > 0 && selectedDocumentIds.size < documents.length

	return (
		<div>
			{/* Selection controls */}
			{selectedDocumentIds.size > 0 && (
				<div className="flex items-center gap-2 mb-4">
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button variant="destructive" disabled={isBatchDeleting}>
								<Trash2 className="mr-2 h-4 w-4" />
								Delete Selected ({selectedDocumentIds.size})
								{isBatchDeleting && "..."}
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
								<AlertDialogDescription>
									This action cannot be undone. This will permanently delete the
									following {selectedDocumentIds.size} document(s):
									<ul className="mt-2 list-disc list-inside max-h-60 overflow-y-auto">
										{selectedDocuments.map((doc) => (
											<li key={doc.id} className="truncate">
												{doc.name}
											</li>
										))}
									</ul>
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel disabled={isBatchDeleting}>
									Cancel
								</AlertDialogCancel>
								<AlertDialogAction
									onClick={handleConfirmBatchDelete}
									disabled={isBatchDeleting}
									className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								>
									{isBatchDeleting ? "Deleting..." : "Yes, delete"}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
					<Button
						variant="outline"
						onClick={() => setSelectedDocumentIds(new Set())}
					>
						Cancel
					</Button>
				</div>
			)}

			{/* Document count summary */}
			{documents.length > 0 && (
				<div className="text-sm text-muted-foreground mb-2">
					{documents.length} document
					{documents.length !== 1 ? "s" : ""}
				</div>
			)}

			{/* Table in scrollable container */}
			<div className="border rounded-md overflow-hidden">
				{/* Fixed header - Need to ensure column widths match exactly with body table */}
				<div className="bg-background border-b">
					<table className="w-full table-fixed">
						<thead>
							<tr>
								<th className="w-[40px] h-10 py-2 px-4 text-left font-medium text-muted-foreground">
									<EnhancedCheckbox
										checked={allSelected}
										indeterminate={someSelected}
										onCheckedChange={handleSelectAll}
										aria-label="Select all documents"
									/>
								</th>
								<th
									className="w-[30%] h-10 py-2 px-4 text-left font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
									onClick={() => onSort("name")}
								>
									<div className="flex items-center">
										Name
										<span className="ml-1">
											{sortField === "name" ? (
												sortDirection === "asc" ? (
													<ArrowUp className="h-3.5 w-3.5" />
												) : (
													<ArrowDown className="h-3.5 w-3.5" />
												)
											) : (
												<ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground/50" />
											)}
										</span>
									</div>
								</th>
								<th
									className="w-[120px] h-10 py-2 px-4 text-left font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
									onClick={() => onSort("type")}
								>
									<div className="flex items-center">
										Type
										<span className="ml-1">
											{sortField === "type" ? (
												sortDirection === "asc" ? (
													<ArrowUp className="h-3.5 w-3.5" />
												) : (
													<ArrowDown className="h-3.5 w-3.5" />
												)
											) : (
												<ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground/50" />
											)}
										</span>
									</div>
								</th>
								<th
									className="w-[100px] h-10 py-2 px-4 text-left font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
									onClick={() => onSort("size")}
								>
									<div className="flex items-center">
										Size
										<span className="ml-1">
											{sortField === "size" ? (
												sortDirection === "asc" ? (
													<ArrowUp className="h-3.5 w-3.5" />
												) : (
													<ArrowDown className="h-3.5 w-3.5" />
												)
											) : (
												<ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground/50" />
											)}
										</span>
									</div>
								</th>
								<th
									className="w-[150px] h-10 py-2 px-4 text-left font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
									onClick={() => onSort("created_at")}
								>
									<div className="flex items-center">
										Uploaded
										<span className="ml-1">
											{sortField === "created_at" ? (
												sortDirection === "asc" ? (
													<ArrowUp className="h-3.5 w-3.5" />
												) : (
													<ArrowDown className="h-3.5 w-3.5" />
												)
											) : (
												<ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground/50" />
											)}
										</span>
									</div>
								</th>
								<th
									className="w-[80px] h-10 py-2 px-4 text-left font-medium text-muted-foreground cursor-pointer hover:bg-muted/50"
									onClick={() => onSort("relevance")}
								>
									<div className="flex items-center">
										Status
										<span className="ml-1">
											{sortField === "relevance" ? (
												sortDirection === "asc" ? (
													<ArrowUp className="h-3.5 w-3.5" />
												) : (
													<ArrowDown className="h-3.5 w-3.5" />
												)
											) : (
												<ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground/50" />
											)}
										</span>
									</div>
								</th>
								{hasSearchRelevance && (
									<th className="w-[100px] h-10 py-2 px-4 text-left font-medium text-muted-foreground">
										Search Relevance
									</th>
								)}
								<th className="w-[80px] h-10 py-2 px-4 text-right font-medium text-muted-foreground">
									Actions
								</th>
							</tr>
						</thead>
					</table>
				</div>

				{/* Scrollable body - Match column widths with header table */}
				<div className="overflow-y-auto" style={{ maxHeight }}>
					<table className="w-full table-fixed">
						<tbody>
							{documents.length === 0 ? (
								<tr className="border-b">
									<td
										colSpan={hasSearchRelevance ? 8 : 7}
										className="h-24 text-center p-4"
									>
										No documents found.
									</td>
								</tr>
							) : (
								documents.map((document) => (
									<DocumentRow
										key={document.id}
										document={document}
										caseId={caseId}
										isSelected={selectedDocumentIds.has(document.id)}
										onToggleSelect={() => handleToggleSelect(document.id)}
										onDelete={() => onDeleteDocument(document.id)}
										stagesData={stagesData?.[document.id]}
									/>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}
